import { APP_STORE_URL } from '@aurora/core'
import { SaplingMark } from '../components/ui/SaplingMark'

/**
 * The front door for someone who has never used Sapling: what it is, the
 * iPhone app as the main way in, and the web app beside it. Everything past
 * this page (sign in, start, join) is the Welcome flow's own steps.
 */

const STEPS = [
  { n: '1', title: 'Tell it what happened', body: 'Dictate a note, photograph their work, or mark an activity done.' },
  { n: '2', title: 'It plans what comes next', body: 'Short activities aimed at exactly where your child is now.' },
  { n: '3', title: 'Everyone raising them sees it', body: 'Parents, grandparents, the nanny: one shared record, each entry signed.' },
]

export function Landing({ busy, onSignIn, onCreate, onJoin, onDemo }: {
  busy: boolean
  onSignIn: () => void
  onCreate: () => void
  onJoin: () => void
  onDemo: () => void
}) {
  return (
    <div className="landing">
      <div className="welcome-sky" aria-hidden="true">
        <div className="welcome-band welcome-band-1" />
        <div className="welcome-band welcome-band-2" />
      </div>

      <header className="landing-bar">
        <span className="landing-brand">
          <SaplingMark size={28} tone="brand" />
          Sapling
        </span>
        <button className="btn btn-ghost btn-sm" onClick={onSignIn}>Sign in</button>
      </header>

      <main className="landing-hero">
        <div className="landing-copy">
          <h1 className="landing-title">A thinking partner for raising a child deliberately.</h1>
          <p className="landing-lede">
            It remembers everything you tell it, notices what you cannot, and makes
            the next activity, game or plan you need.
          </p>

          <div className="landing-get">
            <a className="landing-badge" href={APP_STORE_URL} aria-label="Download Sapling Family on the App Store">
              <img src="/landing/app-store-badge.svg" alt="" width="180" height="60" />
            </a>
            <a className="landing-qr" href={APP_STORE_URL} aria-label="App Store link as a QR code">
              <img src="/landing/app-store-qr.svg" alt="" width="64" height="64" />
              <span>Scan with your iPhone camera</span>
            </a>
          </div>

          <div className="landing-web">
            <span className="landing-web-label">Or on the web</span>
            <button className="btn btn-primary btn-sm" onClick={onCreate}>Start a family</button>
            <button className="btn btn-ghost btn-sm" onClick={onJoin}>Join with a code</button>
            <button className="btn btn-quiet btn-sm" onClick={onDemo} disabled={busy}>
              {busy ? 'Setting up…' : 'Try the demo'}
            </button>
          </div>
        </div>

        <div className="landing-phones" aria-hidden="true">
          <figure className="landing-phone landing-phone-back">
            <img src="/landing/timeline.jpg" alt="" />
          </figure>
          <figure className="landing-phone landing-phone-front">
            <img src="/landing/thread.jpg" alt="" />
          </figure>
        </div>
      </main>

      <section className="landing-steps" aria-label="How it works">
        {STEPS.map((s) => (
          <div key={s.n} className="landing-step">
            <span className="landing-step-n">{s.n}</span>
            <h2>{s.title}</h2>
            <p>{s.body}</p>
          </div>
        ))}
      </section>

      <footer className="landing-foot">
        <span>Sapling Family · Free · No ads, no tracking</span>
        <nav>
          <a href={APP_STORE_URL}>App Store</a>
          <a href="/privacy">Privacy</a>
          <a href="/support">Support</a>
        </nav>
      </footer>
    </div>
  )
}
